import { useQuery } from '@tanstack/react-query';
import { getStainTypeFilteredIds } from 'api/stainTypes';
import LabelledDropdown from 'components/atoms/Dropdown/LabelledDropdown';
import { Study } from 'interfaces/study';
import { first, map, size } from 'lodash';
import React from 'react';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { useStainTypeIdToDisplayName } from 'utils/useStainTypeIdToDisplayName';

export const DefaultStainTypeSelection: React.FC<
  React.PropsWithChildren<{
    studyId: Study['id'];
    updatedSettings: Study['settings'];
    setUpdatedSettings: React.Dispatch<React.SetStateAction<Study['settings']>>;
    editable?: boolean;
  }>
> = ({ studyId, updatedSettings, setUpdatedSettings, editable = true }) => {
  const { labId } = useCurrentLabId();
  const caseParams = { labId, filters: { studyId } };
  const { data: stains, isLoading } = useQuery({
    queryKey: ['slidesStainsTypes', caseParams],
    queryFn: ({ signal }) => getStainTypeFilteredIds(caseParams, signal),
  });
  const { stainTypeIdToDisplayName, isLoadingStainTypeOptions } = useStainTypeIdToDisplayName();

  const stainsOptions = [
    ...map(
      stains || (updatedSettings.defaultStainingType ? [updatedSettings.defaultStainingType] : []),
      (currStain) => ({ value: currStain, text: stainTypeIdToDisplayName(currStain) })
    ),
    { value: '', text: 'None' },
  ];

  const unsetStainingTypeValue = size(stainsOptions) === 1 ? first(stainsOptions).value : '';
  const currentDefaultStainingType = updatedSettings.defaultStainingType || unsetStainingTypeValue;

  return (
    <LabelledDropdown
      key={size(stainsOptions)}
      disabled={size(stainsOptions) < 2 || !editable}
      label="Stain Type Selection"
      options={stainsOptions}
      value={currentDefaultStainingType}
      onOptionSelected={(optionValue) => {
        setUpdatedSettings((prevSettings) => ({
          ...prevSettings,
          defaultStainingType: optionValue,
        }));
      }}
      loading={isLoading || isLoadingStainTypeOptions}
      placeholder={currentDefaultStainingType}
    />
  );
};
